'use client';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import {
  CheckCircle2,
  CloudDownload,
  Database,
  FolderOpen,
  Search,
  ServerCog,
  Trash2,
  Upload,
  RotateCw,
  WifiOff,
  X,
} from 'lucide-react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Toaster, toast } from 'sonner';
import { getFileContent } from './api';
import { FileHistoryDrawer } from './file-history-drawer';
import { FilePreview } from './file-preview';
import { FileTreeView } from './file-tree-view';
import { LegacySandboxSection } from './legacy-sandbox-section';
import {
  buildFileTree,
  collectFolderPaths,
  filterTree,
  formatBytes,
  totalSize,
} from './tree';
import { useWorkspace } from './workspace-context';

interface UploadChip {
  id: string;
  name: string;
  status: 'uploading' | 'done' | 'error';
  error?: string;
}

export function WorkspaceFilesTab({ sandboxOutput }: { sandboxOutput?: unknown }) {
  const ws = useWorkspace();
  const {
    resolvePhase,
    resolveError,
    retryResolve,
    filesPhase,
    filesError,
    refreshFiles,
    files,
    selectedPath,
    selectFile,
    selectedFile,
    workspace,
    upload,
    remove,
    snapshot,
  } = ws;

  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [uploads, setUploads] = useState<UploadChip[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [previewVersion, setPreviewVersion] = useState<number | undefined>(undefined);
  const [snapshotting, setSnapshotting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fullTree = useMemo(() => buildFileTree(files), [files]);
  const tree = useMemo(() => filterTree(fullTree, query), [fullTree, query]);
  const expandedForRender = useMemo(
    () => (query.trim() ? new Set(collectFolderPaths(tree)) : expanded),
    [query, tree, expanded],
  );

  // Reset the historical-version pin whenever the selected file changes.
  const handleSelect = useCallback(
    (path: string) => {
      selectFile(path);
      setPreviewVersion(undefined);
    },
    [selectFile],
  );

  const runUploads = useCallback(
    async (fileList: File[]) => {
      for (const file of fileList) {
        const id = `${file.name}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
        setUploads((prev) => [...prev, { id, name: file.name, status: 'uploading' }]);
        const res = await upload(file, file.name);
        setUploads((prev) =>
          prev.map((u) =>
            u.id === id
              ? { ...u, status: res.ok ? 'done' : 'error', error: res.ok ? undefined : res.error }
              : u,
          ),
        );
        if (res.ok) {
          toast.success(`Uploaded ${file.name}`);
          // Auto-dismiss the success chip after a moment.
          setTimeout(() => setUploads((prev) => prev.filter((u) => u.id !== id)), 2500);
        } else {
          toast.error(`Upload failed: ${res.error}`);
        }
      }
    },
    [upload],
  );

  const onPickFiles = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const list = e.target.files ? Array.from(e.target.files) : [];
      if (list.length) void runUploads(list);
      e.target.value = '';
    },
    [runUploads],
  );

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragActive(false);
      const list = e.dataTransfer?.files ? Array.from(e.dataTransfer.files) : [];
      if (list.length) void runUploads(list);
    },
    [runUploads],
  );

  const doDownload = useCallback(async () => {
    if (!workspace || !selectedFile) return;
    const res = await getFileContent(workspace.id, selectedFile.path);
    if (!res.ok) {
      toast.error(`Could not download: ${res.error}`);
      return;
    }
    const a = document.createElement('a');
    a.href = res.data.url;
    a.download = selectedFile.path.split('/').pop() ?? 'download';
    a.rel = 'noopener';
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }, [workspace, selectedFile]);

  const doDelete = useCallback(async () => {
    const path = confirmDelete;
    setConfirmDelete(null);
    if (!path) return;
    const res = await remove(path);
    if (res.ok) toast.success(`Deleted ${path.split('/').pop()}`);
    else toast.error(`Delete failed: ${res.error}`);
  }, [confirmDelete, remove]);

  const doSnapshot = useCallback(async () => {
    setSnapshotting(true);
    const res = await snapshot();
    setSnapshotting(false);
    if (res.ok) {
      const { filesPulled, filesSkipped, truncated } = res.data;
      toast.success(
        `Pulled ${filesPulled} file${filesPulled === 1 ? '' : 's'} from sandbox` +
          (filesSkipped ? `, skipped ${filesSkipped}` : '') +
          (truncated ? ' (truncated)' : ''),
      );
    } else if (res.status === 409) {
      toast.info('No live sandbox to snapshot from.');
    } else if (res.notAvailable) {
      toast.info('Workspace storage is not available yet.');
    } else {
      toast.error(`Snapshot failed: ${res.error}`);
    }
  }, [snapshot]);

  // ---- Whole-panel states (resolve) ----
  if (resolvePhase === 'loading' || resolvePhase === 'idle') {
    return <CenteredState icon={<Spinner className="size-5" />} title="Opening workspace…" />;
  }
  if (resolvePhase === 'unavailable') {
    return (
      <CenteredState
        icon={<Database className="size-6 text-muted-foreground" />}
        title="Workspace storage not available yet"
        body={resolveError ?? 'Your files will appear here once the workspace store is online.'}
        action={{ label: 'Retry', onClick: retryResolve }}
      />
    );
  }
  if (resolvePhase === 'error') {
    return (
      <CenteredState
        icon={<WifiOff className="size-6 text-amber-500" />}
        title="Couldn't open the workspace"
        body={resolveError ?? undefined}
        action={{ label: 'Retry', onClick: retryResolve }}
      />
    );
  }

  const fileCount = files.length;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <Toaster position="bottom-right" richColors closeButton />

      {/* Toolbar */}
      <div className="flex shrink-0 flex-wrap items-center gap-1.5 border-b border-border bg-muted/20 px-3 py-2">
        <Button variant="outline" size="sm" className="h-8 gap-1.5" onClick={() => fileInputRef.current?.click()}>
          <Upload className="size-3.5" /> Upload
        </Button>
        <input ref={fileInputRef} type="file" multiple className="hidden" onChange={onPickFiles} aria-label="Upload files" />
        <Button
          variant="outline"
          size="sm"
          className="h-8 gap-1.5"
          onClick={doDownload}
          disabled={!selectedFile}
        >
          <CloudDownload className="size-3.5" /> Download
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="h-8 gap-1.5 text-destructive hover:text-destructive"
          onClick={() => selectedPath && setConfirmDelete(selectedPath)}
          disabled={!selectedFile}
        >
          <Trash2 className="size-3.5" /> Delete
        </Button>
        <div className="mx-1 h-5 w-px bg-border" />
        <Button variant="ghost" size="sm" className="h-8 gap-1.5" onClick={refreshFiles}>
          <RotateCw className="size-3.5" /> Refresh
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 gap-1.5"
          onClick={doSnapshot}
          disabled={snapshotting}
        >
          {snapshotting ? <Spinner className="size-3.5" /> : <ServerCog className="size-3.5" />}
          Snapshot from sandbox
        </Button>
      </div>

      {/* Upload chips */}
      {uploads.length > 0 && (
        <div className="flex shrink-0 flex-wrap gap-1.5 border-b border-border bg-background px-3 py-2">
          {uploads.map((u) => (
            <span
              key={u.id}
              data-upload-status={u.status}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs',
                u.status === 'uploading' && 'border-border text-muted-foreground',
                u.status === 'done' && 'border-emerald-500/30 text-emerald-500',
                u.status === 'error' && 'border-destructive/40 text-destructive',
              )}
            >
              {u.status === 'uploading' && <Spinner className="size-3" />}
              {u.status === 'done' && <CheckCircle2 className="size-3" />}
              {u.status === 'error' && <X className="size-3" />}
              <span className="max-w-[12rem] truncate">{u.name}</span>
              {u.status === 'error' && (
                <button
                  type="button"
                  className="underline"
                  onClick={() => setUploads((prev) => prev.filter((x) => x.id !== u.id))}
                >
                  dismiss
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      {/* Split body */}
      <div className="flex min-h-0 flex-1 flex-col md:flex-row">
        {/* Tree column */}
        <div
          className={cn(
            'flex min-h-0 flex-col border-border md:w-72 md:shrink-0 md:border-r',
            'max-md:max-h-[45%] max-md:border-b',
            dragActive && 'ring-2 ring-inset ring-primary/60',
          )}
          onDragOver={(e) => {
            e.preventDefault();
            setDragActive(true);
          }}
          onDragLeave={() => setDragActive(false)}
          onDrop={onDrop}
        >
          {/* Filter */}
          <div className="relative shrink-0 p-2">
            <Search className="pointer-events-none absolute left-4 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter files…"
              className="h-8 pl-8 text-sm"
              aria-label="Filter files"
            />
          </div>

          <div className="min-h-0 flex-1 overflow-auto px-2 pb-2">
            {filesPhase === 'loading' && (
              <div className="flex items-center gap-2 p-3 text-sm text-muted-foreground">
                <Spinner className="size-4" /> Loading files…
              </div>
            )}
            {filesPhase === 'unavailable' && (
              <p className="p-3 text-sm text-muted-foreground">Workspace storage not available yet.</p>
            )}
            {filesPhase === 'error' && (
              <div className="flex flex-col items-start gap-2 p-3">
                <p className="text-sm text-muted-foreground">{filesError}</p>
                <Button variant="outline" size="sm" className="gap-1.5" onClick={refreshFiles}>
                  <RotateCw className="size-3.5" /> Retry
                </Button>
              </div>
            )}
            {filesPhase === 'ready' && files.length === 0 && (
              <div className="p-4 text-center">
                <FolderOpen className="mx-auto size-6 text-muted-foreground" />
                <p className="mt-2 text-sm font-medium">No files yet</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Upload a file or drop it here to get started.
                </p>
              </div>
            )}
            {filesPhase === 'ready' && files.length > 0 && tree.length === 0 && (
              <p className="p-3 text-sm text-muted-foreground">No files match “{query}”.</p>
            )}
            {filesPhase === 'ready' && tree.length > 0 && (
              <FileTreeView
                nodes={tree}
                selectedPath={selectedPath}
                onSelect={handleSelect}
                expanded={expandedForRender}
                onExpandedChange={setExpanded}
              />
            )}
          </div>

          {/* Footer */}
          <div className="shrink-0 border-t border-border px-3 py-1.5 text-[11px] text-muted-foreground">
            <span data-testid="files-footer">
              {fileCount} file{fileCount === 1 ? '' : 's'} · {formatBytes(totalSize(files))}
            </span>
          </div>
        </div>

        {/* Preview column */}
        <div className="min-h-0 flex-1">
          {workspace && selectedFile ? (
            <FilePreview
              workspaceId={workspace.id}
              file={selectedFile}
              version={previewVersion}
              onOpenHistory={() => setHistoryOpen(true)}
            />
          ) : (
            <div className="grid h-full place-items-center p-6 text-center">
              <div>
                <FolderOpen className="mx-auto size-7 text-muted-foreground/60" />
                <p className="mt-3 text-sm font-medium">Select a file to preview</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Code, markdown, HTML, PDFs, images, video, audio and CSV render here — no sandbox required.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Legacy live-sandbox section */}
      <LegacySandboxSection output={sandboxOutput} />

      {/* History drawer */}
      {workspace && (
        <FileHistoryDrawer
          open={historyOpen}
          onOpenChange={setHistoryOpen}
          workspaceId={workspace.id}
          filePath={selectedPath}
          activeVersion={previewVersion}
          onViewVersion={(v) => {
            setPreviewVersion(v);
            setHistoryOpen(false);
          }}
        />
      )}

      {/* Delete confirm */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this file?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-mono">{confirmDelete}</span> will be removed from the workspace.
              This cannot be undone from here.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              className="bg-destructive text-white hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function CenteredState({
  icon,
  title,
  body,
  action,
}: {
  icon: React.ReactNode;
  title: string;
  body?: string;
  action?: { label: string; onClick: () => void };
}) {
  return (
    <div className="flex h-full min-h-0 items-center justify-center p-6 text-center">
      <div className="max-w-sm">
        <span className="mx-auto grid size-11 place-items-center rounded-lg border bg-muted/30">
          {icon}
        </span>
        <p className="mt-3 text-sm font-medium">{title}</p>
        {body && <p className="mt-1 text-sm text-muted-foreground">{body}</p>}
        {action && (
          <Button variant="outline" size="sm" className="mt-3 gap-1.5" onClick={action.onClick}>
            <RotateCw className="size-3.5" /> {action.label}
          </Button>
        )}
      </div>
    </div>
  );
}
